import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControlOptions, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StatusCode } from '@app/_enums/status-code';
import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { Status } from '@models/status.model';
import { VoucherConfigurationType } from '@models/voucher-configuration-type.model';
import { TranslateService } from '@ngx-translate/core';
import { NomenclatureService } from '@services/nomenclature.service';
import { BehaviorSubject, EMPTY, Subscription, forkJoin } from 'rxjs';
import { catchError, concatMap, repeatWhen, tap } from 'rxjs/operators';
import { VoucherService } from '@app/vouchers/_services/voucher.service';
import { VoucherServicesComponent } from './voucher-services/voucher-services.component';
import { VoucherTechnicalPointsComponent } from './voucher-technical-points/voucher-technical-points.component';
import { DateValidators } from '@app/_validators/dates-validator';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { Voucher } from '../_models/voucher.model';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { formatFullDate } from '@app/_utils/date-util';
import { RegisterOfLegalPersonsModalComponent } from '@components/register-of-legal-persons-modal/register-of-legal-persons-modal.component';
import { YesNoMessageboxComponent } from '@components/_base/_messageboxes/yes-no-messagebox/yes-no-messagebox.component';
import { modalMinWidth } from '@env/config';
import { MatDialog } from '@angular/material/dialog';
import { SubjectVersion } from '@models/subject-version.model';
import { latinAndDigitsValidator } from '@app/_utils/form-util';

@Component({
  selector: 'app-add-edit-voucher',
  templateUrl: './add-edit-voucher.component.html',
  styleUrls: ['./add-edit-voucher.component.css']
})
export class AddEditVoucherComponent extends BaseParentComponent implements OnInit {
  // Units
  statuses:                   Status[];
  defaultStatus:              Status | null;
  voucher:                    Voucher;
  voucherConfigurationTypes:  VoucherConfigurationType[];

  // Constants
  readonly INACTIVE_STATUS_CODE:      string = 'INACTIVE';
  readonly ACTIVE_STATUS_CODE:        string = 'ACTIVE';
  readonly POINTS_TYPE_CODE:          string = 'POINTS';
  readonly REFERRAL_TYPE_CODE:        string = 'REFERRAL';
  readonly WELCOME_WHEEL_TYPE_CODE:   string = 'WELCOME_WHEEL';

  // Payload
  private subscriptions = new Subscription();
  serviceCategoriesIds: Array<number> = [];
  todayDate:            Date = new Date();
  rows:                 number;
  statusCode:           string;
  voucherId:            number;
  subject:              SubjectVersion | null;

  // Booleans
  dataLoaded        = false;
  disableActionBtn  = false;
  refreshingCodes   = false;

  // Issued voucher codes still pointing at an older version of this voucher
  staleCodesCount   = 0;

  // Property decorators
  @ViewChild('services') servicesComponent:               VoucherServicesComponent;
  @ViewChild('technicalPoints') technicalPointComponent:  VoucherTechnicalPointsComponent;

  // Form
  mainDataForm = this.formBuilder.group({
    id:             [null],
    validFrom:      [null, Validators.required],
    validTo:        [null, Validators.required],
    status:         [null, Validators.required],
    versionData:    [null],
    validPeriodDays: [null]
  }, { validator: [DateValidators.groupValidator('validFrom', 'validTo')] } as AbstractControlOptions);

  // Observables
  changedCategoriesServices = new BehaviorSubject<number[]>([]);
  changedCategories$        = this.changedCategoriesServices.asObservable();

  loadVoucher$ = this.route.queryParams.pipe(
    tap(() => this.addControlsToMainForm()),
    concatMap(params => {
      return forkJoin([
        this.voucherService.findVoucherById(params['id']),
        this.nomenclatureService.getStatusesByType(this.checkStatuses()),
        this.nomenclatureService.getVoucherConfigurationTypes()
      ]).pipe(
        tap(() => this.voucherId = params['id']),
        tap(([voucher, statuses, voucherConfigurationTypes]) => {
          this.statuses = statuses;
          this.voucherConfigurationTypes = voucherConfigurationTypes;

          this.voucher = voucher;
          this.statusCode = voucher?.status?.code;
          this.defaultStatus = this.statuses.find(status => status.code === this.INACTIVE_STATUS_CODE) || null;

          this.mainDataForm.patchValue(voucher);

          this.mainDataForm.get('status')?.patchValue(!!voucher && !!voucher.status ? voucher.status : this.defaultStatus);

          this.mainDataForm.get('voucherConfigurationType')?.patchValue(voucher?.voucherConfigurationType ?? null);
          this.applyTypeVisibility(voucher?.voucherConfigurationType?.code);

          if (voucher?.priceAttributeKind == 'P') {
            this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          } else if (voucher?.priceAttributeKind == 'V') {
            this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          } else {
            this.mainDataForm.get('priceAttributeAmountDds')?.clearValidators();
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          }

          this.mainDataForm.get('priceAttributeAmountDds')?.patchValue(voucher?.priceAttributeAmountDds);
          this.mainDataForm.get('validFrom')?.patchValue(new Date(voucher?.validFrom));
          this.mainDataForm.get('validTo')?.patchValue(new Date(voucher?.validTo));

          if (this.statusCode == this.ACTIVE_STATUS_CODE) {
            this.mainDataForm.disable();
          }
          this.dataLoaded = true;
          this.loadStaleCodesCount();
        }),
        catchError(err => {
          displayError(err);
          this.dataLoaded = false;
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          return EMPTY;
        }),
        repeatWhen(() => this.reload$)
      );
    })
  );

  constructor(
    private router:               Router,
    private route:                ActivatedRoute,
    private formBuilder:          FormBuilder,
    private uiEvent:              UIEventCustom,
    public perms:                 PermissionsService,
    private translateService:     TranslateService,
    private voucherService:       VoucherService,
    private nomenclatureService:  NomenclatureService,
    private dialog:               MatDialog,
  ) {
    super(router);
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data
    });
  }

  async save(deactivate: boolean) {
    if (this.mainDataForm.get('status')?.value?.code === this.INACTIVE_STATUS_CODE) {
      if (!this.partualValidation()) {
        this.uiEvent.displayUIError();
        this.showValidationsSubject.next(true);
        this.showFullValidationSubject.next(false);
        return;
      }
    } else {
      await Promise.all([this.servicesComponent.initialize(), this.technicalPointComponent.initialize()]);

      await new Promise(f => setTimeout(f, 400));
      this.mainDataForm.enable();
      this.servicesComponent.servicesForms.enable();

      if (!deactivate) {
        if (!this.fullValidation()) {
          this.uiEvent.displayUIError();
          this.showValidationsSubject.next(true);
          this.showFullValidationSubject.next(true);
          return;
        }
      }
    }

    this.disableActionBtn = true;
    this.voucherService.saveVoucher(this.constructVoucher(), deactivate).subscribe({
      next: () => {
        this.uiEvent.displayUISuccess();
        this.disableActionBtn = false;
        this.router.navigate(['/list-vouchers']);
      },
      error: err => {
        displayError(err);
        this.disableActionBtn = false;
        this.uiEvent.displayUIError();
      }
    });
  }

  private partualValidation() {
    return this.mainDataForm.valid;
  }

  private fullValidation() {
    let formValid = true;

    if (!this.mainDataForm.valid) {
      formValid = false;
    }

    if (this.servicesComponent.isInitialized) {
      if (!this.servicesComponent.validateServices()) {
        formValid = false;
      }
    }

    if (this.technicalPointComponent.isInitialized) {
      if (!this.technicalPointComponent.validateTechPoints()) {
        formValid = false;
      }
    }

    return formValid;
  }

  checkStatuses() {
    return StatusCode.PromotionDiscount;
  }

  changeVoucherType(event: any) {
    const code = this.mainDataForm.get('voucherConfigurationType')?.value?.code;
    this.applyTypeVisibility(code);
  }

  private applyTypeVisibility(typeCode: string | undefined) {
    const points = this.mainDataForm.get('points');
    const inspectionsCount = this.mainDataForm.get('inspectionsCount');

    if (typeCode === this.REFERRAL_TYPE_CODE) {
      points?.clearValidators();
      points?.setValue(null);
      inspectionsCount?.setValidators([Validators.required, Validators.min(1), Validators.max(10000)]);
    } else if (typeCode === this.POINTS_TYPE_CODE) {
      inspectionsCount?.clearValidators();
      inspectionsCount?.setValue(null);
      points?.setValidators([Validators.required, Validators.min(1), Validators.max(10000)]);
    } else if (typeCode === this.WELCOME_WHEEL_TYPE_CODE) {
      // Welcome wheel vouchers are neither bought with points nor earned by inspections,
      // so both fields are hidden and must be persisted as null.
      points?.clearValidators();
      points?.setValue(null);
      inspectionsCount?.clearValidators();
      inspectionsCount?.setValue(null);
    } else {
      points?.clearValidators();
      inspectionsCount?.clearValidators();
    }

    points?.updateValueAndValidity();
    inspectionsCount?.updateValueAndValidity();
  }

  get selectedTypeCode(): string | undefined {
    return this.mainDataForm.get('voucherConfigurationType')?.value?.code;
  }

  changePriceAttrKind(event: any) {
    let type = event.target.value;

    if (type == 'P') {
      this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
    } else if (type == 'V') {
      this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
      this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
    }
  }

  handleCountRowEvent(rows: number) {
    this.rows = rows;
  }

  private addControlsToMainForm() {
    // Add voucher form controls
    this.mainDataForm.addControl('name', this.formBuilder.control(null, [Validators.required, Validators.maxLength(30)]));
    this.mainDataForm.addControl('voucherPrefix', this.formBuilder.control(null, [Validators.required, Validators.maxLength(20), latinAndDigitsValidator]));
    this.mainDataForm.addControl('priceAttributeKind', this.formBuilder.control(null, Validators.required));
    this.mainDataForm.addControl('priceAttributeAmountDds', this.formBuilder.control(null, [Validators.required, Validators.max(100)]));
    this.mainDataForm.addControl('voucherConfigurationType', this.formBuilder.control(null, Validators.required));
    this.mainDataForm.addControl('points', this.formBuilder.control(null, [Validators.required, Validators.min(1), Validators.max(10000)]));
    this.mainDataForm.addControl('inspectionsCount', this.formBuilder.control(null));
    this.mainDataForm.addControl('validPeriodDays', this.formBuilder.control(null, [Validators.required, Validators.min(1), Validators.max(365)]));
    this.mainDataForm.addControl('information', this.formBuilder.control(null, [Validators.maxLength(500)]));
    this.mainDataForm.addControl('clarification', this.formBuilder.control(null, [Validators.maxLength(500)]));
  }

  private constructVoucher(): Voucher {
    const voucher = this.mainDataForm.value as Voucher;

    voucher.validFrom = formatFullDate(this.mainDataForm.get('validFrom')?.value);
    voucher.validTo = formatFullDate(this.mainDataForm.get('validTo')?.value);
    voucher.services = this.servicesComponent.constructServices();
    voucher.voucherTechnicalPoints = this.technicalPointComponent.constructVoucherTechPoints();

    return voucher;
  }

  public deactivate() {
    this.save(true);
  }

  /**
   * Only an active voucher forks a new version on save, so only its codes can be left behind.
   * The count is loaded outside the main forkJoin - it is an indicator, not data the screen depends on.
   */
  private loadStaleCodesCount() {
    this.staleCodesCount = 0;

    if (this.voucherId == null || this.statusCode != this.ACTIVE_STATUS_CODE) {
      return;
    }

    this.voucherService.countStaleVoucherCodes(this.voucherId).subscribe({
      next: count => this.staleCodesCount = count ?? 0,
      error: err => displayError(err)
    });
  }

  async refreshCodes() {
    const dialogRef = this.dialog.open(YesNoMessageboxComponent, {
      ...modalMinWidth,
      data: {
        isWarning: false,
        message: 'messagebox.refreshVoucherCodes'
      }
    });

    const isConfirm = await dialogRef.afterClosed().toPromise();
    if (!isConfirm) {
      return;
    }

    this.refreshingCodes = true;
    this.voucherService.refreshVoucherCodes(this.voucherId).subscribe({
      next: () => {
        this.uiEvent.displayUISuccess();
        this.refreshingCodes = false;
        this.loadStaleCodesCount();
      },
      error: err => {
        displayError(err);
        this.refreshingCodes = false;
        this.uiEvent.displayUIError();
      }
    });
  }

  openModal() {
    if (this.dialog.openDialogs.length == 0) {
      let ref = this.dialog.open(RegisterOfLegalPersonsModalComponent, modalMinWidth);
      this.subscriptions.add(ref.componentInstance.submitSubject.subscribe(result => {
        this.mainDataForm.get('subject')?.setValue(result);
        this.subject = result;
      }));
    }
  }

  clearSubject() {
    this.mainDataForm.get('subject')?.setValue(null);
    this.subject = null;
  }

  get mainForm() {
    return this.mainDataForm.controls;
  }

  get servicesComponentState() {
    if (this.servicesComponent == null) return false;
    return this.servicesComponent.isOpened;
  }

  get tpComponentState() {
    if (this.technicalPointComponent == null) return false;
    return this.technicalPointComponent.isOpened;
  }

  async initializeSection(code: string) {
    switch (code) {
      case 'technicalPoints': {
        await this.servicesComponent.initialize();
        this.technicalPointComponent.initialize();
        break;
      }
      case 'services': {
        await this.servicesComponent.initialize();
        if (this.voucherId != null) {
          await this.technicalPointComponent.initialize()
        }
        break;
      }
      default: { }
    }
  }

  get isDisabled() {
    return this.statusCode == this.ACTIVE_STATUS_CODE
  }

  get canAddEdit() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  }

}
